import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { useTheme } from '../context/ThemeContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePlayerScores, PlayerScoreInfo } from '../hooks/usePlayerScores';
import { getSharedScenario, claimSharedPool } from '../api/api';
import { SharedScenarioData, WinnerWithMetadata, WinnerSource } from '../types/types';
import ResponsiveBracketVisualizer from './ResponsiveBracketVisualizer';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { Loader2, Copy, Check, ExternalLink, ChevronDown } from 'lucide-react';
import Pagination from './Pagination';

const PAGE_SIZE = 20;

const SharedScenarioView: React.FC = () => {
  const { shareCode } = useParams<{ shareCode: string }>();
  const { currentUser } = useAuth();
  const { applyPoolScoring } = useConfig();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [sharedData, setSharedData] = useState<SharedScenarioData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimedPoolName, setClaimedPoolName] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  usePageMeta({
    title: sharedData ? `${sharedData.scenarioName} — My Bracket Sim` : 'Shared Scenario — My Bracket Sim',
    description: sharedData
      ? `View shared bracket scenario "${sharedData.scenarioName}" from pool "${sharedData.poolName}".`
      : 'View a shared bracket scenario on My Bracket Sim.',
    path: `/s/${shareCode}`,
  });

  // Fetch shared scenario data
  useEffect(() => {
    if (!shareCode) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getSharedScenario(shareCode);
        setSharedData(data);
        // Apply the shared scenario's point values
        applyPoolScoring(data.pointValues);
      } catch (err: any) {
        setError(err.message || 'Failed to load shared scenario');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [shareCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build WinnerWithMetadata array from the snapshot
  const winnersWithMetadata: WinnerWithMetadata[] = useMemo(() => {
    if (!sharedData) return [];
    return sharedData.knownWinners.map(team => ({
      team: team || '',
      source: WinnerSource.SCENARIO,
    }));
  }, [sharedData]);

  const { playerScores, rankMap } = usePlayerScores(
    sharedData?.playerPicks || '',
    winnersWithMetadata
  );

  // Split top 5 into featured (#1, possibly tied) and runners-up
  const top5 = playerScores.slice(0, 5);
  const featured = top5.filter(p => rankMap.get(p.name) === 1);
  const runnersUp = top5.filter(p => rankMap.get(p.name) !== 1);

  // Pagination
  const totalPages = Math.ceil(playerScores.length / PAGE_SIZE);
  const paginatedScores = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return playerScores.slice(start, start + PAGE_SIZE);
  }, [playerScores, currentPage]);

  // No-op for read-only bracket
  const noopBracketChange = useCallback(() => {}, []);

  const handleClaim = async () => {
    if (!shareCode) return;
    setClaiming(true);
    try {
      const result = await claimSharedPool(shareCode);
      setClaimed(true);
      setClaimedPoolName(result.poolName);
    } catch (err: any) {
      setError(err.message || 'Failed to copy pool');
    } finally {
      setClaiming(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error / not found state
  if (error || !sharedData) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="flex items-center gap-1 mb-6 justify-center">
            <span className="text-muted-foreground text-lg font-mono">(</span>
            <span className="font-mono text-7xl font-bold tracking-tighter text-primary">404</span>
            <span className="text-muted-foreground text-lg font-mono">)</span>
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-3">Scenario Not Found</h1>
          <p className="text-muted-foreground mb-8">
            This shared scenario doesn't exist or may have been removed.
          </p>
          <Button size="lg" onClick={() => navigate('/')}>
            Go to My Bracket Sim
          </Button>
        </div>
      </div>
    );
  }

  const filledGames = sharedData.knownWinners.filter(w => w && w.trim() !== '').length;

  return (
    <div className="min-h-screen bg-background">
      {/* Scoped styles: block all clicks inside bracket except accordion triggers */}
      <style>{`
        .shared-bracket-readonly * { pointer-events: none !important; }
        .shared-bracket-readonly [data-state="open"],
        .shared-bracket-readonly [data-state="closed"],
        .shared-bracket-readonly [data-state="open"] > *,
        .shared-bracket-readonly [data-state="closed"] > * { pointer-events: auto !important; }
        .shared-bracket-readonly .overflow-x-auto { pointer-events: auto !important; touch-action: pan-x pan-y; }
      `}</style>

      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center gap-3">
            <a href="/">
              <img
                src={theme === 'dark' ? '/bracketsim_header_dark.png' : '/bracketsim_header.png'}
                alt="My Bracket Sim"
                className="h-[72px] sm:h-[84px] w-auto object-contain"
              />
            </a>
            <div className="h-5 w-px bg-border" />
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-foreground truncate">
                {sharedData.poolName}
              </h1>
              <p className="text-xs text-muted-foreground">
                {sharedData.scenarioName}
                <span className="mx-1.5">·</span>
                {filledGames}/63 games
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      <div className="bg-primary/5 border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          {currentUser ? (
            // Authenticated user — offer to copy the pool
            claimed ? (
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                <Check className="h-4 w-4" />
                <span>Pool copied as "{claimedPoolName}"</span>
                <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Open App
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-foreground">
                  Want to use this pool? Copy it to your account and build your own scenarios.
                </p>
                <Button size="sm" onClick={handleClaim} disabled={claiming}>
                  {claiming && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                  <Copy className="h-3 w-3 mr-1" />
                  Copy Pool to My Account
                </Button>
              </>
            )
          ) : (
            // Not authenticated — encourage signup
            <>
              <p className="text-sm text-foreground">
                Build your own bracket scenarios and find out how you can still win.
              </p>
              <Button size="sm" onClick={() => navigate(`/login?ref=share&code=${shareCode}`)}>
                Sign up and use this pool
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Partial scenario notice */}
      {filledGames < 63 && (
        <div className="bg-amber-50 dark:bg-amber-950/30">
          <div className="max-w-4xl mx-auto px-4 py-2 text-center text-sm text-amber-700 dark:text-amber-400">
            Scenario incomplete ({filledGames}/63 games filled) — some scores may change as remaining games are decided.
          </div>
        </div>
      )}

      {/* Top 5 Preview */}
      {playerScores.length > 0 && (
        <div className="border-b">
          <div className="max-w-sm mx-auto px-4 py-5">
            {/* #1 — featured (handles ties) */}
            {featured.length > 0 && (
              <div className="flex flex-col items-center text-center mb-4">
                <span className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">
                  {featured.length > 1 ? 'Tied for 1st' : '1st Place'}
                </span>
                {featured.map((player, idx) => (
                  <div key={player.name} className={idx > 0 ? 'mt-1' : ''}>
                    <div className="text-2xl font-bold text-foreground">{player.name}</div>
                  </div>
                ))}
                <span className="text-lg font-semibold text-primary tabular-nums mt-0.5">
                  {featured[0].scenarioScore} pts
                </span>
              </div>
            )}
            {/* Runners-up */}
            {runnersUp.length > 0 && (
              <div className="flex flex-col gap-1.5">
                {runnersUp.map((player) => {
                  const rank = rankMap.get(player.name) ?? 0;
                  return (
                    <div
                      key={player.name}
                      className="flex items-center gap-3 rounded-md border bg-card px-3 py-2"
                    >
                      <span className="text-sm font-bold text-muted-foreground tabular-nums w-5 text-right">{rank}</span>
                      <span className="text-sm font-medium text-foreground truncate flex-1">{player.name}</span>
                      <span className="text-sm text-muted-foreground tabular-nums">{player.scenarioScore} pts</span>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Jump link */}
            <div className="mt-3 text-center">
              <a
                href="#full-standings"
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
              >
                Full standings
                <ChevronDown className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Bracket Visualization (read-only: block game clicks but allow accordion expand/collapse) */}
      <div className="shared-bracket-readonly w-full px-4 py-4">
        <ResponsiveBracketVisualizer
          winnersWithMetadata={winnersWithMetadata}
          onUserBracketChanges={noopBracketChange}
        />
      </div>

      {/* Standings */}
      <div id="full-standings" className="max-w-4xl mx-auto px-4 py-6 scroll-mt-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Full Standings</CardTitle>
          </CardHeader>
          <CardContent>
            {playerScores.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No player data available.
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-7 text-right pr-1 px-0.5">#</TableHead>
                        <TableHead className="text-left px-1">Player</TableHead>
                        <TableHead className="text-center px-0.5 leading-tight">Score</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedScores.map((player) => {
                        const rank = rankMap.get(player.name) ?? 0;
                        return (
                          <TableRow key={player.name} className="hover:bg-muted/50">
                            <TableCell className="text-right pr-1 px-0.5 text-muted-foreground text-xs tabular-nums">
                              {rank}
                            </TableCell>
                            <TableCell className="px-1 text-sm font-medium truncate max-w-[200px]">
                              {player.name}
                            </TableCell>
                            <TableCell className="text-center px-0.5 text-sm tabular-nums font-semibold">
                              {player.scenarioScore}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {totalPages > 1 && (
                  <Pagination
                    totalItems={playerScores.length}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    pageSize={PAGE_SIZE}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Footer CTA for non-authenticated users */}
        {!currentUser && (
          <div className="mt-8 text-center">
            <p className="text-muted-foreground text-sm mb-4">
              Build your own bracket scenarios and find out how you can still win.
            </p>
            <Button size="lg" onClick={() => navigate(`/login?ref=share&code=${shareCode}`)}>
              Sign up and use this pool
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedScenarioView;
